<template>
  <div class="grid">
    <!-- KPI stat cards -->
    <div
      v-for="stat in statCards"
      :key="stat.label"
      class="col-12 md:col-6 lg:col-3"
    >
      <div class="card mb-0">
        <div class="flex justify-content-between mb-3">
          <div>
            <span class="block text-500 font-medium mb-3">{{ stat.label }}</span>
            <div class="text-900 font-medium text-xl">
              {{ stat.value }}
            </div>
          </div>
          <div
            class="flex align-items-center justify-content-center border-round"
            :style="{ width: '2.5rem', height: '2.5rem', backgroundColor: stat.bg }"
          >
            <i
              :class="stat.icon + ' text-xl'"
              :style="{ color: stat.color }"
            />
          </div>
        </div>
        <span class="text-500">{{ stat.sub }}</span>
      </div>
    </div>

    <!-- Practice progress -->
    <div class="col-12 lg:col-6">
      <div class="card h-full">
        <div class="flex justify-content-between align-items-center mb-4">
          <h5 class="m-0">
            Practice progress
          </h5>
          <Button
            label="Start practice"
            icon="pi pi-play"
            class="p-button-sm"
            :disabled="!totalQuestions"
            @click="$router.push('/practice')"
          />
        </div>

        <div
          v-if="totalQuestions"
          class="mb-4"
        >
          <div class="flex justify-content-between mb-2">
            <span class="text-500">Practiced at least once</span>
            <span class="text-900 font-medium">{{ answeredCount }} / {{ totalQuestions }} ({{ practicedPct }}%)</span>
          </div>
          <div class="progress-track">
            <div
              class="progress-fill"
              :style="{ width: practicedPct + '%' }"
            />
          </div>
        </div>

        <div class="grid text-center mt-4">
          <div class="col-4">
            <div class="text-900 text-2xl font-medium">
              {{ masteredCount }}
            </div>
            <span class="text-500 text-sm">Mastered</span>
          </div>
          <div class="col-4">
            <div class="text-900 text-2xl font-medium">
              {{ answeredCount }}
            </div>
            <span class="text-500 text-sm">Practiced</span>
          </div>
          <div class="col-4">
            <div class="text-900 text-2xl font-medium">
              {{ newCount }}
            </div>
            <span class="text-500 text-sm">Not started</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Questions by group -->
    <div class="col-12 lg:col-6">
      <div class="card h-full">
        <h5>Questions by group</h5>
        <div v-if="groupStats.length">
          <div
            v-for="g in groupStats"
            :key="g.id"
            class="mb-3"
          >
            <div class="flex justify-content-between mb-1">
              <span class="text-900">{{ g.title }}</span>
              <span class="text-500">{{ g.answered }} / {{ g.total }} practiced</span>
            </div>
            <div class="progress-track">
              <div
                class="progress-fill"
                :style="{ width: g.pct + '%' }"
              />
            </div>
          </div>
        </div>
        <p
          v-else
          class="text-500"
        >
          No groups yet. Create one on the Groups page.
        </p>
      </div>
    </div>

    <!-- Needs practice -->
    <div class="col-12">
      <div class="card">
        <div class="flex justify-content-between align-items-center mb-3">
          <div class="flex align-items-center gap-2">
            <h5 class="m-0">
              Needs practice
            </h5>
            <Dropdown
              v-model="needsPracticeLimit"
              :options="limitOptions"
              class="p-inputtext-sm"
              aria-label="Number of questions to show"
              @change="loadNeedsPractice"
            />
          </div>
          <Button
            label="Manage questions"
            icon="pi pi-book"
            class="p-button-text p-button-sm"
            @click="$router.push('/questions')"
          />
        </div>

        <DataTable
          :value="needsPractice"
          data-key="id"
          :loading="practiceLoading"
        >
          <template #empty>
            <div class="text-center p-4 text-500">
              <i class="pi pi-inbox text-2xl mb-2 block" />
              No questions yet. Add some on the Questions page to start practicing.
            </div>
          </template>
          <Column
            field="question"
            header="Question"
          />
          <Column
            field="answer"
            header="Answer"
          />
          <Column header="Group">
            <template #body="{ data }">
              {{ groupTitle(data.group_id) }}
            </template>
          </Column>
          <Column header="Correct count">
            <template #body="{ data }">
              {{ data.correct_answer_cnt || 0 }}
            </template>
          </Column>
          <Column header="Last practiced">
            <template #body="{ data }">
              {{ data.answered_at ? formatDate(data.answered_at) : 'Never' }}
            </template>
          </Column>
        </DataTable>
      </div>
    </div>
  </div>
</template>

<script>
import { dashboardService } from '../service'

export default {
  data () {
    return {
      dashboard: null,
      loading: true,
      needsPractice: [],
      practiceLoading: true,
      needsPracticeLimit: 10,
      limitOptions: [10, 20, 30, 40, 50]
    }
  },
  computed: {
    groups () {
      return this.dashboard ? this.dashboard.groups : []
    },
    totalQuestions () {
      return this.dashboard ? this.dashboard.total_questions : 0
    },
    answeredCount () {
      return this.dashboard ? this.dashboard.answered : 0
    },
    masteredCount () {
      return this.dashboard ? this.dashboard.mastered : 0
    },
    masteryThreshold () {
      return this.dashboard ? this.dashboard.mastery_threshold : 3
    },
    newCount () {
      return this.totalQuestions - this.answeredCount
    },
    practicedPct () {
      if (!this.totalQuestions) return 0
      return Math.round((this.answeredCount / this.totalQuestions) * 100)
    },
    statCards () {
      const cards = [
        { label: 'Groups', value: this.groups.length, icon: 'pi pi-folder', color: '#6366F1', sub: 'Vocabulary sets' },
        { label: 'Questions', value: this.totalQuestions, icon: 'pi pi-book', color: '#F59E0B', sub: 'Words to learn' },
        { label: 'Practiced', value: this.answeredCount, icon: 'pi pi-check-circle', color: '#22C55E', sub: this.practicedPct + '% of all questions' },
        { label: 'Mastered', value: this.masteredCount, icon: 'pi pi-star', color: '#8B5CF6', sub: this.masteryThreshold + '+ correct answers' }
      ]
      // Tint each icon tile with a translucent version of its own color so it
      // reads well in both light and dark mode.
      return cards.map(card => ({ ...card, bg: card.color + '22' }))
    },
    groupStats () {
      return this.groups.map(group => ({
        id: group.id,
        title: group.title,
        total: group.total,
        answered: group.answered,
        pct: group.total ? Math.round((group.answered / group.total) * 100) : 0
      }))
    }
  },
  mounted () {
    dashboardService.getDashboard()
      .then((resp) => {
        this.dashboard = resp.data
      })
      .finally(() => {
        this.loading = false
      })
    this.loadNeedsPractice()
  },
  methods: {
    loadNeedsPractice () {
      this.practiceLoading = true
      return dashboardService.getNeedsPractice(this.needsPracticeLimit)
        .then((resp) => {
          this.needsPractice = resp.data
        })
        .finally(() => {
          this.practiceLoading = false
        })
    },
    groupTitle (groupId) {
      const group = this.groups.find(g => g.id === groupId)
      return group ? group.title : '—'
    },
    formatDate (value) {
      if (!value) return ''
      return new Date(value).toLocaleDateString('en-US', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      })
    }
  }
}
</script>

<style scoped>
.progress-track {
  height: 0.5rem;
  background: var(--surface-200);
  border-radius: 0.25rem;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: var(--primary-color);
  border-radius: 0.25rem;
  transition: width 0.3s ease;
}
</style>
